import clientServices from './clientServices';

// Login pelo Guardian ID (OIDC, code + PKCE S256), contrato oidc-endpoints v2.0 do guardian-identity.
const services = {};

const GID_ENDPOINT_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

const base64Url = bytes => {
  let binary = '';
  bytes.forEach(b => { binary += String.fromCharCode(b); });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const randomString = cryptoImpl => base64Url(cryptoImpl.getRandomValues(new Uint8Array(32)));

// O EndpointName vem só do caminho do aplicativo (mesma regra usada para achar o cliente no FrameApi);
// query e hash são ignorados. Sem endpoint, "default" ou fora do formato: null (não enviar).
services.resolveGidEndpoint = href => {
  const url = new URL(href);
  const endPoint = clientServices.resolveEndpoint(url.origin + url.pathname);
  if (endPoint === 'default' || !GID_ENDPOINT_PATTERN.test(endPoint)) return null;
  return endPoint;
};

// Quem decide é só a marca "Guardian ID habilitado" do cliente (GuardianIdEnabled da ErpParams), que o
// automatizador-api devolve em GET client/{endpoint}. O modo de login não liga o Guardian ID.
services.usesGuardianId = client => !!client && client.guardianIdEnabled === true;

// Depois de sair do aplicativo, o próximo authorize leva prompt=login (o Guardian ID pede a senha de novo,
// mesmo com sessão SSO). A marca fica até um login concluído.
services.sessionForReload = session => ({ ...session, logoff: false, gidPromptLogin: !!(session.logoff || session.gidPromptLogin) });

services.sessionAfterLogin = session => ({ ...session, gidPromptLogin: false });

const isHttpsUrl = value => {
  try {
    return new URL(value).protocol === 'https:';
  } catch (e) {
    return false;
  }
};

const hasText = value => typeof value === 'string' && value.trim() !== '';

// Configuração incompleta, endereço fora de https ou página sem criptografia do navegador (fora de HTTPS)
// são erro de configuração, não indisponibilidade temporária.
services.checkConfig = (config, cryptoImpl = window.crypto) => {
  if (!config || !['authority', 'clientId', 'redirectUri', 'scope'].every(campo => hasText(config[campo]))) return false;
  if (!isHttpsUrl(config.authority) || !isHttpsUrl(config.redirectUri)) return false;
  return !!cryptoImpl && !!cryptoImpl.subtle && typeof cryptoImpl.getRandomValues === 'function';
};

services.computeChallenge = async (verifier, cryptoImpl = window.crypto) => {
  const digest = await cryptoImpl.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64Url(new Uint8Array(digest));
};

services.createPkce = async (cryptoImpl = window.crypto) => {
  const verifier = randomString(cryptoImpl);
  return {
    verifier,
    challenge: await services.computeChallenge(verifier, cryptoImpl),
    state: randomString(cryptoImpl),
    nonce: randomString(cryptoImpl)
  };
};

services.buildAuthorizeUrl = (config, endpoint, pkce, options = {}) => {
  if (!endpoint || !GID_ENDPOINT_PATTERN.test(endpoint)) {
    throw new Error('gid_endpoint ausente ou inválido.');
  }
  const url = new URL('connect/authorize', config.authority);
  const params = {
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    response_type: 'code',
    scope: config.scope,
    code_challenge: pkce.challenge,
    code_challenge_method: 'S256',
    state: pkce.state,
    nonce: pkce.nonce,
    gid_endpoint: endpoint
  };
  if (options.promptLogin) params.prompt = 'login';
  url.search = '';
  Object.keys(params).forEach(key => url.searchParams.set(key, params[key]));
  return url.toString();
};

// Resultado de erro: kind = 'temporario' (tentar de novo), 'configuracao' (cadastro/config do app) ou 'login'.
const failure = kind => ({ ok: false, kind });

const ERROR_KINDS = {
  temporarily_unavailable: 'temporario',
  invalid_request: 'configuracao',
  invalid_client: 'configuracao',
  unsupported_grant_type: 'configuracao'
};

const errorKind = error => ERROR_KINDS[error] || 'login';

services.parseCallback = (callback, pending) => {
  if (callback.error) return failure(errorKind(callback.error));
  if (!pending || !callback.code || callback.state !== pending.state) return failure('login');
  return { ok: true, code: callback.code };
};

services.exchangeCode = async (config, code, verifier, fetchImpl = fetch) => {
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    code,
    redirect_uri: config.redirectUri,
    client_id: config.clientId,
    code_verifier: verifier
  });

  let res;
  let data;
  try {
    res = await fetchImpl(new URL('connect/token', config.authority).toString(), {
      method: 'POST',
      // Cliente público: a troca não leva cookies (o CORS do Guardian ID não libera credenciais).
      credentials: 'omit',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString()
    });
    data = await res.json();
  } catch (e) {
    // Rede, CORS ou corpo inválido.
    return failure('temporario');
  }

  if (res.ok && data && data.access_token) return { ok: true, tokens: data };
  if (res.status === 503) return failure('temporario');
  return failure(errorKind(data && data.error));
};

const decodePayload = token => {
  const part = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(part);
  const bytes = Uint8Array.from(binary, c => c.charCodeAt(0));
  return JSON.parse(new TextDecoder().decode(bytes));
};

// Sem verificação de assinatura: quem valida o access token é a API. Aqui só se confere o vínculo com
// este login (nonce) e com o cliente da Guardian do caminho (IDClient do sub).
services.validateTokens = (tokens, pending, clientId) => {
  try {
    const idToken = decodePayload(tokens.id_token);
    const accessToken = decodePayload(tokens.access_token);
    const [idClient] = String(accessToken.sub).split(':');
    if (idToken.nonce !== pending.nonce || idClient !== String(clientId) || !accessToken.unique_name) {
      return failure('login');
    }
    return { ok: true, userName: accessToken.unique_name, accessToken: tokens.access_token, expiresIn: tokens.expires_in };
  } catch (e) {
    return failure('login');
  }
};

export default services;
